import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CHART_COLORS, inferFormat, formatValue } from '../charts/chartUtils';
import { CopyImageButton } from './CopyImageButton';
import { LocalStorage } from '../../utils/LocalStorage';

const PIE_TOP_N = 8;
const MAX_LABEL_LENGTH = 40;

function truncateLabel(label, max = MAX_LABEL_LENGTH) {
    if (!label || label.length <= max) return label;
    return label.substring(0, max - 1) + '…';
}

/**
 * Label personalizado para cada slice, muestra el deltaPercentage cuando hay comparación.
 */
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, deltaPercentage }) => {
    if (deltaPercentage == null) return null;
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.25;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const sign = deltaPercentage >= 0 ? '+' : '';
    const color = deltaPercentage >= 0 ? '#259D92' : '#DF0D00';

    return (
        <text x={x} y={y} fill={color} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={11} fontWeight={600}>
            {sign}{deltaPercentage.toFixed(1)}%
        </text>
    );
};

function buildPieData(tableData, dimensionName, metricName, t) {
    if (!tableData || !tableData.rows || !dimensionName || !metricName) return null;

    const dimKey = `dimension_${dimensionName}`;
    const metricKey = `metric_${metricName}`;
    const groups = {};

    tableData.rows.forEach(row => {
        const dimCell = row[dimKey];
        const dimValue = (dimCell && (dimCell.formattedValue || dimCell.value)) || 'Sin dato';
        const cell = row[metricKey];

        if (!groups[dimValue]) {
            groups[dimValue] = { current: 0, comparison: 0, hasComparison: false };
        }

        if (cell) {
            // Con comparación: currentValue; sin comparación: value
            const v = cell.currentValue ?? cell.value;
            if (v != null && !isNaN(v)) {
                groups[dimValue].current += Number(v);
            }
            if (cell.comparisonValue != null) {
                groups[dimValue].comparison += cell.comparisonValue;
                groups[dimValue].hasComparison = true;
            }
        }
    });

    let entries = Object.entries(groups);
    const total = entries.reduce((sum, [, v]) => sum + v.current, 0);
    const hasComparison = entries.some(([, v]) => v.hasComparison);

    if (total === 0) return null;

    // Ordenar por valor desc y agrupar top N + "Otras"
    entries.sort(([, a], [, b]) => b.current - a.current);
    const topEntries = entries.slice(0, PIE_TOP_N);
    const restEntries = entries.slice(PIE_TOP_N);

    if (restEntries.length > 0) {
        const othersCurrent = restEntries.reduce((s, [, v]) => s + v.current, 0);
        const othersComp = restEntries.reduce((s, [, v]) => s + v.comparison, 0);
        const othersHasComp = restEntries.some(([, v]) => v.hasComparison);
        topEntries.push([t('data_explorer.others', 'Otras'), {
            current: othersCurrent, comparison: othersComp, hasComparison: othersHasComp
        }]);
    }

    return topEntries.map(([name, vals]) => {
        const percent = total > 0 ? (vals.current / total) * 100 : 0;
        let deltaPercentage = null;
        if (hasComparison && vals.comparison !== 0) {
            deltaPercentage = ((vals.current - vals.comparison) / Math.abs(vals.comparison)) * 100;
        }
        return {
            name: truncateLabel(name),
            value: vals.current,
            percent,
            deltaPercentage,
        };
    });
}

class DataExplorerPieChartComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedDimension: null,
            selectedMetric: null,
        };
        this.handleDimensionChange = this.handleDimensionChange.bind(this);
        this.handleMetricChange = this.handleMetricChange.bind(this);
    }

    componentDidMount() {
        this.initDefaults();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.lastFilters !== this.props.lastFilters) {
            this.syncSelections(prevProps.lastFilters);
        }
    }

    syncSelections(prevFilters) {
        const { lastFilters } = this.props;
        if (!lastFilters) return;

        const prevDims = (prevFilters && prevFilters.dimensions) || [];
        const prevMetrics = (prevFilters && prevFilters.metrics) || [];
        const newDims = lastFilters.dimensions || [];
        const newMetrics = lastFilters.metrics || [];

        const dimsChanged = prevDims.join(',') !== newDims.join(',');
        const metricsChanged = prevMetrics.join(',') !== newMetrics.join(',');

        if (!dimsChanged && !metricsChanged) return;

        const { selectedDimension, selectedMetric } = this.state;

        this.setState({
            selectedDimension: newDims.includes(selectedDimension)
                ? selectedDimension
                : (newDims.length > 0 ? newDims[newDims.length - 1] : null),
            selectedMetric: newMetrics.includes(selectedMetric)
                ? selectedMetric
                : (newMetrics[0] || null),
        });
    }

    // `chartPrefs` explícitas (portal del cliente) ganan sobre las del navegador: ahí las
    // preferencias son parte de la vista que armó la agencia, no del historial de quien mira.
    resolveChartPrefs() {
        if (this.props.chartPrefs) return this.props.chartPrefs;
        return LocalStorage.getDataExplorerChartPreferences();
    }

    initDefaults() {
        const { lastFilters } = this.props;
        if (!lastFilters) return;

        const dims = lastFilters.dimensions || [];
        const metrics = lastFilters.metrics || [];

        const prefs = this.resolveChartPrefs();
        if (prefs) {
            const savedDim = prefs.pieChartDimension && dims.includes(prefs.pieChartDimension)
                ? prefs.pieChartDimension : null;
            const savedMetric = prefs.pieChartMetric && metrics.includes(prefs.pieChartMetric)
                ? prefs.pieChartMetric : null;

            if (savedDim || savedMetric) {
                this.setState({
                    selectedDimension: savedDim || (dims.length > 0 ? dims[dims.length - 1] : null),
                    selectedMetric: savedMetric || (metrics[0] || null),
                });
                return;
            }
        }

        this.setState({
            selectedDimension: dims.length > 0 ? dims[dims.length - 1] : null,
            selectedMetric: metrics[0] || null,
        });
    }

    getFieldLabel(name) {
        const { fieldsData } = this.props;
        if (!fieldsData || !fieldsData.FIELD_DATA_BY_ID || !name) return name || '';
        const fd = fieldsData.FIELD_DATA_BY_ID[name];
        return fd ? fd.name : name;
    }

    handleDimensionChange(e) {
        const dim = e.target.value;
        this.setState({ selectedDimension: dim });
        this.savePieChartPreference({ pieChartDimension: dim });
    }

    handleMetricChange(e) {
        const metric = e.target.value;
        this.setState({ selectedMetric: metric });
        this.savePieChartPreference({ pieChartMetric: metric });
    }

    savePieChartPreference(update) {
        if (!this.props.chartPrefs) {
            const prefs = LocalStorage.getDataExplorerChartPreferences() || {};
            LocalStorage.saveDataExplorerChartPreferences({ ...prefs, ...update });
        }
        if (this.props.onChartPrefsChange) this.props.onChartPrefsChange();
    }

    renderTooltip({ active, payload }) {
        if (!active || !payload || !payload.length) return null;
        const entry = payload[0].payload;
        const { selectedMetric } = this.state;
        const format = inferFormat(selectedMetric || '');
        return (
            <div className="de-pie-tooltip">
                <strong>{entry.name}</strong>
                <div>{formatValue(entry.value, format)} ({entry.percent.toFixed(1)}%)</div>
            </div>
        );
    }

    render() {
        const { t, results } = this.props;
        const { selectedDimension, selectedMetric } = this.state;
        const { lastFilters } = this.props;
        const dims = (lastFilters && lastFilters.dimensions) || [];
        const metrics = (lastFilters && lastFilters.metrics) || [];

        if (dims.length === 0) {
            return (
                <div className="de-chart-block">
                    <div className="de-chart-block-header">
                        <h3>{t('data_explorer.pie_chart', 'Distribución')}</h3>
                    </div>
                    <p className="de-chart-no-data">{t('data_explorer.select_dimension', 'Seleccioná al menos una dimensión')}</p>
                </div>
            );
        }

        const tableData = results && results.tableData;
        const pieData = buildPieData(tableData, selectedDimension, selectedMetric, t);
        const hasComparison = pieData && pieData.some(d => d.deltaPercentage != null);

        return (
            <CopyImageButton>
                {(copyBtn) => (
                <div className="de-chart-block">
                    <div className="de-chart-block-header">
                        <h3>{t('data_explorer.pie_chart', 'Distribución')}</h3>
                        <div className="de-chart-controls">
                            {/* Selector de dimensión */}
                            <select
                                className="form-control form-control-sm"
                                value={selectedDimension || ''}
                                onChange={this.handleDimensionChange}
                            >
                                {dims.map(d => (
                                    <option key={d} value={d}>{this.getFieldLabel(d)}</option>
                                ))}
                            </select>

                            {/* Selector de métrica */}
                            <select
                                className="form-control form-control-sm"
                                value={selectedMetric || ''}
                                onChange={this.handleMetricChange}
                            >
                                {metrics.map(m => (
                                    <option key={m} value={m}>{this.getFieldLabel(m)}</option>
                                ))}
                            </select>
                            {copyBtn}
                        </div>
                    </div>

                    {pieData && pieData.length > 0 ? (
                        <div style={{ flex: 1, minHeight: 300 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={pieData}
                                    dataKey="value"
                                    nameKey="name"
                                    cx="50%"
                                    cy="45%"
                                    outerRadius={90}
                                    label={hasComparison ? renderCustomLabel : undefined}
                                    labelLine={hasComparison}
                                >
                                    {pieData.map((entry, index) => (
                                        <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={this.renderTooltip.bind(this)} />
                                <Legend
                                    layout="horizontal"
                                    verticalAlign="bottom"
                                    align="center"
                                    formatter={(value) => truncateLabel(value, 35)}
                                    wrapperStyle={{ fontSize: '0.75rem', paddingTop: '10px' }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        </div>
                    ) : (
                        <p className="de-chart-no-data">{t('data_explorer.no_data', 'No hay datos para mostrar.')}</p>
                    )}
                </div>
                )}
            </CopyImageButton>
        );
    }
}

export const DataExplorerPieChart = withTranslation()(DataExplorerPieChartComponent);
